// src/pages/DashboardHome.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import { useNotification } from "../../context/NotificationProvider";
import usePermissions from "../../hooks/usePermissions";
import {
  AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { getData } from "../../services/Fetchnodeservices";

// ===================== CUSTOM COMPONENTS =====================
const Card = ({ children, style, onClick }) => (
  <div
    onClick={onClick}
    style={{
      ...style,
      transition: "all 0.3s ease",
      cursor: onClick ? "pointer" : "default",
      position: "relative",
      overflow: "hidden",
    }}
    onMouseEnter={(e) => {
      if (onClick) {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 12px 40px rgba(108, 99, 255, 0.15)";
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    }}
  >
    {children}
  </div>
);

const CardHeader = ({ children, style }) => (
  <div style={{ padding: "16px 20px 0", ...style }}>{children}</div>
);

const CardTitle = ({ children, style }) => (
  <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700, ...style }}>{children}</h3>
);

const CardContent = ({ children, style }) => (
  <div style={{ padding: "16px 20px 20px", ...style }}>{children}</div>
);

const Badge = ({ children, style }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      borderRadius: "6px",
      padding: "2px 8px",
      fontSize: "11px",
      fontWeight: 600,
      ...style,
    }}
  >
    {children}
  </span>
);

const Button = ({ children, onClick, variant = "default", size = "default", style }) => {
  const baseStyle = {
    cursor: "pointer",
    borderRadius: "8px",
    fontWeight: 500,
    transition: "all 0.15s ease",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    ...(size === "icon" && { width: "36px", height: "36px", padding: 0 }),
    ...(size === "sm" && { padding: "4px 12px", fontSize: "11px", height: "28px" }),
    ...(size === "default" && { padding: "8px 16px", fontSize: "13px" }),
    ...(variant === "outline" && { background: "transparent", border: "1px solid" }),
    ...(variant === "ghost" && { background: "transparent", border: "none" }),
    ...style,
  };
  return (
    <button onClick={onClick} style={baseStyle}>
      {children}
    </button>
  );
};

const Separator = ({ orientation = "horizontal", style }) => (
  <div
    style={{
      ...(orientation === "horizontal"
        ? { height: "1px", width: "100%" }
        : { width: "1px", height: "100%" }),
      ...style,
    }}
  />
);

// ===================== CHART TOOLTIP (Theme-Aware) =====================
const ChartTip = ({ active, payload, label, theme }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: "10px",
        padding: "10px 14px",
        boxShadow: `0 8px 32px ${theme.primary}20`,
        fontSize: "12px",
      }}
    >
      {label && (
        <p style={{ color: theme.textMuted, marginBottom: "6px", fontWeight: 600 }}>
          {label}
        </p>
      )}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontWeight: 600, margin: "2px 0" }}>
          {p.name}: {typeof p.value === "number" ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

const CHART_COLORS = ["#6c63ff", "#4fd1c5", "#f97316", "#ec4899", "#94a3b8", "#0ea5e9"];

// ===================== STAT CARD COMPONENT (Theme-Aware) =====================
const StatCard = ({
  icon,
  label,
  value,
  change,
  positive,
  bgColor,
  iconBg,
  theme,
  onClick,
  loading,
  subtitle,
  subValue,
}) => {
  return (
    <Card
      onClick={onClick}
      style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: "16px",
        boxShadow: `0 4px 12px ${theme.primary}10`,
        overflow: "hidden",
        position: "relative",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div
        style={{
          position: "absolute",
          right: "-18px",
          top: "-18px",
          width: "96px",
          height: "96px",
          borderRadius: "50%",
          background: `${iconBg || theme.primary}1a`,
          pointerEvents: "none",
        }}
      />
      <CardHeader style={{ paddingBottom: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.6px",
              textTransform: "uppercase",
              color: theme.textMuted,
            }}
          >
            {label}
          </p>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: iconBg || theme.primary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: `0 4px 14px ${iconBg || theme.primary}55`,
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
        </div>
        {loading ? (
          <div
            style={{
              margin: "8px 0 0",
              height: "32px",
              width: "80px",
              background: theme.border,
              borderRadius: "6px",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          />
        ) : (
          <>
            <p
              style={{
                margin: "8px 0 0",
                fontSize: "28px",
                fontWeight: 800,
                color: theme.textPrimary,
                lineHeight: 1.1,
              }}
            >
              {value}
            </p>
            {subtitle && subValue && (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginTop: "6px",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontSize: "10px", color: theme.textMuted }}>
                  {subtitle}:{" "}
                  <strong style={{ color: theme.textPrimary }}>{subValue}</strong>
                </span>
              </div>
            )}
          </>
        )}
      </CardHeader>
      <CardContent style={{ paddingTop: "10px" }}>
        <Separator
          style={{ background: theme.border, marginBottom: "10px" }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {!loading && change && (
            <Badge
              style={{
                background: positive
                  ? `${theme.success}1a`
                  : `${theme.error || theme.danger}1a`,
                color: positive ? theme.success : theme.error || theme.danger,
                border: "none",
                fontSize: "11px",
                fontWeight: 700,
              }}
            >
              {change}
            </Badge>
          )}
          <span style={{ fontSize: "11px", color: theme.textMuted }}>
            vs last month
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// ===================== LIVE ORDER CARD (Theme-Aware) =====================
const LiveOrderCard = ({ order, theme }) => {
  const orderStatus =
    order.order_status_restro || order.order_status_user || "pending";

  const statusConfig = {
    pending: {
      label: "Pending",
      color: theme.warning,
      bg: `${theme.warning}1a`,
    },
    accepted: {
      label: "Accepted",
      color: theme.primary,
      bg: `${theme.primary}1a`,
    },
    preparing: {
      label: "Preparing",
      color: "#8b5cf6",
      bg: "rgba(139,92,246,0.12)",
    },
    ready: {
      label: "Ready",
      color: "#06b6d4",
      bg: "rgba(6,182,212,0.12)",
    },
    out_for_delivery: {
      label: "Out for Delivery",
      color: "#8b5cf6",
      bg: "rgba(139,92,246,0.12)",
    },
    delivered: {
      label: "Delivered",
      color: theme.success,
      bg: `${theme.success}1a`,
    },
    completed: {
      label: "Completed",
      color: theme.success,
      bg: `${theme.success}1a`,
    },
    cancelled: {
      label: "Cancelled",
      color: theme.error || theme.danger,
      bg: `${theme.error || theme.danger}1a`,
    },
  };

  const config = statusConfig[orderStatus] || statusConfig.pending;

  let itemsSummary = "";
  try {
    const items =
      typeof order.items === "string" ? JSON.parse(order.items) : order.items;
    if (Array.isArray(items) && items.length > 0) {
      itemsSummary = items.map((item) => `${item.name} x${item.qty}`).join(", ");
    }
  } catch (e) {
    itemsSummary = order.items || "";
  }

  const restaurantName =
    order.organisation_name || order.restaurant_name || order.designation || "Restaurant";

  return (
    <div
      style={{
        background: theme.surfaceLighter,
        borderRadius: "12px",
        padding: "12px",
        border: `1px solid ${theme.border}`,
        marginBottom: "10px",
        transition: "transform 0.2s",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              fontWeight: 700,
              fontSize: "13px",
              color: theme.textPrimary,
              fontFamily: "monospace",
            }}
          >
            #{order.id}
          </span>
          <Badge
            style={{
              background: config.bg,
              color: config.color,
              border: "none",
              fontSize: "10px",
            }}
          >
            {config.label}
          </Badge>
          {order.pay_status === "paid" && (
            <Badge
              style={{
                background: `${theme.success}1a`,
                color: theme.success,
                border: "none",
                fontSize: "10px",
              }}
            >
              ● Paid
            </Badge>
          )}
        </div>
        <span style={{ fontSize: "11px", color: theme.textMuted }}>
          {order.created_at
            ? new Date(order.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            : ""}
        </span>
      </div>
      <div
        style={{
          fontSize: "12px",
          color: theme.textSecondary,
          marginBottom: "4px",
        }}
      >
        {order.receiver_name || "Customer"} • {restaurantName}
      </div>
      <div
        style={{
          fontSize: "11px",
          color: theme.textMuted,
          marginBottom: "4px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {itemsSummary}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: "13px",
            fontWeight: 700,
            color: theme.primary,
          }}
        >
          ₹{order.final_amount || order.price || 0}
        </span>
        <Button
          size="sm"
          variant="outline"
          style={{
            fontSize: "10px",
            height: "24px",
            padding: "0 10px",
            background: theme.surface,
            borderColor: theme.border,
            color: theme.textSecondary,
          }}
          onClick={() => console.log("View order details:", order.id)}
        >
          View Details
        </Button>
      </div>
    </div>
  );
};

// ===================== MAIN DASHBOARD =====================
const DashboardHome = () => {
  const { theme } = useContext(ThemeContext); // ← Theme Context
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [selectedPeriod, setSelectedPeriod] = useState("weekly");

  const [liveOrders, setLiveOrders] = useState([]);
  const [liveOrdersLoading, setLiveOrdersLoading] = useState(true);

  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalRevenueLoading, setTotalRevenueLoading] = useState(true);

  const [orderStats, setOrderStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    cancelledAmount: 0,
    completedOrdersData: [],
    cancelledOrdersData: [],
  });
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
  });
  const [restaurantStats, setRestaurantStats] = useState({
    totalRestaurants: 0,
    activeRestaurants: 0,
    inactiveRestaurants: 0,
    onRestaurants: 0,
    offRestaurants: 0,
    activeRestaurantData: [],
    onRestaurantData: [],
    offRestaurantData: [],
  });
  const [riderStats, setRiderStats] = useState({
    totalRiders: 0,
    activeRiders: 0,
    inactiveRiders: 0,
    onRiders: 0,
    offRiders: 0,
    onRidersData: [],
    offRidersData: [],
  });
  const [todayStats, setTodayStats] = useState({
    todayOrders: 0,
    todayIncome: 0,
  });
  const [todayOrdersData, setTodayOrdersData] = useState([]);
  const [chartData, setChartData] = useState({ weekly: [], monthly: [], category: [], payment: [], orderStatus: [] });

  const [loading, setLoading] = useState({
    orders: true,
    users: true,
    restaurants: true,
    riders: true,
    today: true,
  });

  // ===================== PERMISSION CHECK HELPERS =====================
  const checkPermissionAndNavigate = (
    permission,
    path,
    state = null,
    errorMessage = "You don't have permission to access this"
  ) => {
    if (hasPermission(permission)) {
      if (state) {
        navigate(path, { state });
      } else {
        navigate(path);
      }
    } else {
      showError("Access Denied", errorMessage);
    }
  };

  const checkAnyPermissionAndNavigate = (
    permissions,
    path,
    state = null,
    errorMessage = "You don't have permission to access this"
  ) => {
    if (hasAnyPermission(permissions)) {
      if (state) {
        navigate(path, { state });
      } else {
        navigate(path);
      }
    } else {
      showError("Access Denied", errorMessage);
    }
  };

  const navigateTo = (path, state = null) => {
    if (state) {
      navigate(path, { state });
    } else {
      navigate(path);
    }
  };

  // ===================== FETCH FUNCTIONS =====================
  const fetchTotalRevenue = async () => {
    try {
      const response = await getData("Dashboard/totalRevenue");
      if (response && response.status === true) {
        // Zero is a valid revenue value. Using `||` here replaced 0 with the
        // whole response object, which rendered as "[object Object]".
        const revenue = Number(response.data?.totalRevenue ?? 0);
        setTotalRevenue(Number.isFinite(revenue) ? revenue : 0);
      } else {
        setTotalRevenue(0);
      }
    } catch (error) {
      console.error("Error fetching total revenue:", error);
      setTotalRevenue(0);
    } finally {
      setTotalRevenueLoading(false);
    }
  };

  const fetchTodayOrdersData = async () => {
    try {
      const response = await getData("Dashboard/todayOrdersData");
      if (response && response.status === true) {
        const data = response.data?.todayOrdersData || response.data || [];
        setTodayOrdersData(data);
      } else {
        setTodayOrdersData([]);
      }
    } catch (error) {
      console.error("Error fetching today orders data:", error);
      setTodayOrdersData([]);
    }
  };

  const fetchLiveOrders = async () => {
    try {
      const response = await getData("Dashboard/today_orders_count");
      if (response && response.status === true && response.data) {
        const ordersData = response.data.last10Orders || [];
        setLiveOrders(ordersData);
      } else {
        setLiveOrders([]);
      }
    } catch (error) {
      console.error("Error fetching live orders:", error);
      setLiveOrders([]);
    } finally {
      setLiveOrdersLoading(false);
    }
  };

  const fetchOrderStats = async () => {
    try {
      const response = await getData("Dashboard/orderstats");
      if (response && response.status === true && response.data) {
        const total = response.data.totalOrders || 0;
        const completed = parseInt(response.data.completedOrders) || 0;
        const cancelled = parseInt(response.data.cancelledOrders) || 0;
        const cancelledAmount = Number(response.data.cancelledAmount || 0);
        const pending = Math.max(0, total - completed - cancelled);

        setOrderStats({
          totalOrders: total,
          pendingOrders: pending,
          completedOrders: completed,
          cancelledOrders: cancelled,
          cancelledAmount: Number.isFinite(cancelledAmount) ? cancelledAmount : 0,
          completedOrdersData: response.data.completedOrdersData || [],
          cancelledOrdersData: response.data.cancelledOrdersData || [],
        });
      }
    } catch (error) {
      console.error("Error fetching order stats:", error);
    } finally {
      setLoading((prev) => ({ ...prev, orders: false }));
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await getData("Dashboard/userstats");
      if (response && response.status === true && response.data) {
        setUserStats({
          totalUsers: response.data.totalUsers || 0,
          activeUsers: parseInt(response.data.activeUsers) || 0,
          inactiveUsers: parseInt(response.data.inactiveUsers) || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching user stats:", error);
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };

  const fetchRestaurantStats = async () => {
    try {
      const response = await getData("Dashboard/restaurantstats");
      if (response && response.status === true && response.data) {
        setRestaurantStats({
          totalRestaurants: response.data.totalRestaurants || 0,
          activeRestaurants: parseInt(response.data.activeRestaurants) || 0,
          inactiveRestaurants: parseInt(response.data.inactiveRestaurants) || 0,
          onRestaurants: parseInt(response.data.onRestaurants) || 0,
          offRestaurants: parseInt(response.data.offRestaurants) || 0,
          activeRestaurantData: response.data.activeRestaurantData || [],
          onRestaurantData: response.data.onRestaurantData || [],
          offRestaurantData: response.data.offRestaurantData || [],
        });
      }
    } catch (error) {
      console.error("Error fetching restaurant stats:", error);
    } finally {
      setLoading((prev) => ({ ...prev, restaurants: false }));
    }
  };

  const fetchRiderStats = async () => {
    try {
      const response = await getData("Dashboard/riderstats");
      if (response && response.status === true && response.data) {
        setRiderStats({
          totalRiders: response.data.totalRiders || 0,
          activeRiders: parseInt(response.data.activeRiders) || 0,
          inactiveRiders: parseInt(response.data.inactiveRiders) || 0,
          onRiders: parseInt(response.data.onRiders) || 0,
          offRiders: parseInt(response.data.offRiders) || 0,
          onRidersData: response.data.onRidersData || [],
          offRidersData: response.data.offRidersData || [],
        });
      }
    } catch (error) {
      console.error("Error fetching rider stats:", error);
    } finally {
      setLoading((prev) => ({ ...prev, riders: false }));
    }
  };

  const fetchTodayStats = async () => {
    try {
      const response = await getData("Dashboard/todaysstats");
      if (response && response.status === true && response.data) {
        setTodayStats({
          todayOrders: response.data.todayOrders || 0,
          todayIncome: response.data.todayIncome || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching today's stats:", error);
    } finally {
      setLoading((prev) => ({ ...prev, today: false }));
    }
  };

  // ===================== NAVIGATION HANDLERS =====================
  const handleCustomersClick = () => {
    if (hasAnyPermission(["users", "users.view"])) {
      navigateTo("/Dashboard/Users");
    } else {
      showError("Access Denied", "You don't have permission to view customers");
    }
  };

  const handleRestaurantClick = () => {
    if (hasAnyPermission(["restaurants", "restaurants.overview"])) {
      const dataToSend = {
        totalRestaurants: restaurantStats.totalRestaurants,
        activeRestaurants: restaurantStats.activeRestaurants,
        inactiveRestaurants: restaurantStats.inactiveRestaurants,
        onRestaurants: restaurantStats.onRestaurants,
        offRestaurants: restaurantStats.offRestaurants,
        onRestaurantData: restaurantStats.onRestaurantData || [],
        offRestaurantData: restaurantStats.offRestaurantData || [],
        activeRestaurantData: restaurantStats.activeRestaurantData,
      };
      navigateTo("/Dashboard/Restaurants", dataToSend);
    } else {
      showError("Access Denied", "You don't have permission to view restaurants");
    }
  };

  const handleLiveRestaurantsClick = () => {
    if (hasAnyPermission(["restaurants", "restaurants.overview"])) {
      const dataToSend = {
        type: "live",
        title: "Live Restaurants",
        totalRestaurants: restaurantStats.totalRestaurants,
        activeRestaurants: restaurantStats.activeRestaurants,
        inactiveRestaurants: restaurantStats.inactiveRestaurants,
        onRestaurants: restaurantStats.onRestaurants,
        offRestaurants: restaurantStats.offRestaurants,
        onRestaurantData: restaurantStats.onRestaurantData || [],
        offRestaurantData: restaurantStats.offRestaurantData || [],
        activeRestaurantData: restaurantStats.activeRestaurantData || [],
      };
      navigateTo("/Dashboard/Restaurants", dataToSend);
    } else {
      showError("Access Denied", "You don't have permission to view live restaurants");
    }
  };

  const handleTotalOrdersClick = () => {
    if (hasAnyPermission(["orders", "orders.view"])) {
      navigateTo("/Dashboard/Orders");
    } else {
      showError("Access Denied", "You don't have permission to view orders");
    }
  };

  const handleCompletedOrdersClick = () => {
    if (hasAnyPermission(["orders", "orders.view"])) {
      const dataToSend = {
        type: "completed",
        count: orderStats.completedOrders,
        data: orderStats.completedOrdersData,
        title: "Completed Orders",
        filter: "completed",
      };
      navigateTo("/Dashboard/Orders", dataToSend);
    } else {
      showError("Access Denied", "You don't have permission to view completed orders");
    }
  };

  const handleCancelledOrdersClick = () => {
    if (hasAnyPermission(["orders", "orders.view"])) {
      const dataToSend = {
        type: "cancelled",
        count: orderStats.cancelledOrders,
        cancelledAmount: orderStats.cancelledAmount,
        data: orderStats.cancelledOrdersData,
        title: "Cancelled Orders",
        filter: "cancelled",
      };
      navigateTo("/Dashboard/Orders", dataToSend);
    } else {
      showError("Access Denied", "You don't have permission to view cancelled orders");
    }
  };

  const handlePendingOrdersClick = () => {
    if (hasAnyPermission(["orders", "orders.view"])) {
      const dataToSend = {
        type: "pending",
        count: orderStats.pendingOrders,
        title: "Pending Orders",
        filter: "pending",
      };
      navigateTo("/Dashboard/Orders", dataToSend);
    } else {
      showError("Access Denied", "You don't have permission to view pending orders");
    }
  };

  const handleDeliveryRidersClick = () => {
    if (hasAnyPermission(["drivers", "drivers.overview"])) {
      const dataToSend = {
        type: "all",
        title: "All Riders",
        totalRiders: riderStats.totalRiders,
        activeRiders: riderStats.activeRiders,
        inactiveRiders: riderStats.inactiveRiders,
        onRiders: riderStats.onRiders,
        offRiders: riderStats.offRiders,
        onRidersData: riderStats.onRidersData,
        offRidersData: riderStats.offRidersData,
      };
      navigateTo("/Dashboard/Drivers", dataToSend);
    } else {
      showError("Access Denied", "You don't have permission to view delivery riders");
    }
  };

  const handleOnlineRidersClick = () => {
    if (hasAnyPermission(["drivers", "drivers.overview"])) {
      const dataToSend = {
        type: "online",
        title: "Online Riders",
        count: riderStats.onRiders,
        data: riderStats.onRidersData,
        totalRiders: riderStats.totalRiders,
        activeRiders: riderStats.activeRiders,
        inactiveRiders: riderStats.inactiveRiders,
        offRiders: riderStats.offRiders,
        offRidersData: riderStats.offRidersData,
      };
      navigateTo("/Dashboard/Drivers", dataToSend);
    } else {
      showError("Access Denied", "You don't have permission to view online riders");
    }
  };

  const handleTodayOrdersClick = () => {
    if (hasAnyPermission(["orders", "orders.view"])) {
      const dataToSend = {
        type: "today",
        title: "Today's Orders",
        count: todayStats.todayOrders,
        data: todayOrdersData,
        totalOrders: orderStats.totalOrders,
        pendingOrders: orderStats.pendingOrders,
        completedOrders: orderStats.completedOrders,
        cancelledOrders: orderStats.cancelledOrders,
      };
      navigateTo("/Dashboard/Orders", dataToSend);
    } else {
      showError("Access Denied", "You don't have permission to view today's orders");
    }
  };

  const handleTotalRevenueClick = () => {
    if (hasPermission("total_revenue")) {
      const dataToSend = {
        type: "total_revenue",
        title: "Total Revenue",
        totalRevenue: totalRevenue,
        todayIncome: todayStats.todayIncome,
        todayOrders: todayStats.todayOrders,
        data: todayOrdersData,
      };
      navigateTo("/Dashboard/Revenue", dataToSend);
    } else {
      showError("Access Denied", "You don't have permission to view total revenue");
    }
  };

  const handleTodayIncomeClick = () => {
    if (hasPermission("total_revenue")) {
      const dataToSend = {
        type: "today_income",
        title: "Today's Income",
        todayIncome: todayStats.todayIncome,
        todayOrders: todayStats.todayOrders,
        data: todayOrdersData,
        totalRevenue: totalRevenue,
      };
      navigateTo("/Dashboard/Revenue", dataToSend);
    } else {
      showError("Access Denied", "You don't have permission to view today's income");
    }
  };

  useEffect(() => {
    fetchOrderStats();
    fetchUserStats();
    fetchRestaurantStats();
    fetchRiderStats();
    fetchTodayStats();
    fetchLiveOrders();
    fetchTotalRevenue();
    fetchTodayOrdersData();
    fetchChartData();

    const interval = setInterval(() => {
      fetchLiveOrders();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  // ===================== FORMATTING FUNCTIONS =====================
  const formatNumber = (num) => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatCurrency = (num) => {
    const value = Number(num);
    if (!Number.isFinite(value)) return "0.00";
    return value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fetchChartData = async () => {
    try {
      const response = await getData("Dashboard/analytics");
      if (response?.status === true && response.data) {
        setChartData({
          weekly: response.data.weekly || [],
          monthly: response.data.monthly || [],
          category: response.data.category || [],
          payment: response.data.payment || [],
          orderStatus: response.data.orderStatus || [],
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard chart data:", error);
    }
  };

  const handleCancelledAmountClick = () => {
    handleCancelledOrdersClick();
  };

  const cancelledOrders = orderStats.cancelledOrders;
  const cancelledAmount = orderStats.cancelledAmount;

  // ===================== DYNAMIC ORDER STATUS DATA =====================
  const getOrderStatusData = () => {
    const colors = { Pending: theme.warning, Completed: theme.success, Cancelled: theme.error || theme.danger };
    return chartData.orderStatus.map((item, index) => ({ ...item, color: colors[item.name] || CHART_COLORS[index % CHART_COLORS.length] }));
  };

  // ===================== STATS CARDS WITH PERMISSIONS =====================
  const stats = [
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
      ),
      label: "Total Orders",
      value: formatNumber(orderStats.totalOrders),
      change: "+11.57%",
      positive: true,
      iconBg: theme.success,
      loading: loading.orders,
      customHandler: handleTotalOrdersClick,
      permission: ["orders", "orders.view"],
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M8 8l8 8M16 8l-8 8" />
        </svg>
      ),
      label: "Cancelled Amount",
      value: `₹${formatCurrency(cancelledAmount)}`,
      change: `${formatNumber(cancelledOrders)} cancelled orders`,
      positive: false,
      iconBg: theme.error || theme.danger,
      loading: loading.orders,
      customHandler: handleCancelledAmountClick,
      permission: ["orders", "orders.view"],
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="5" y1="6" x2="19" y2="6" strokeLinecap="round" />
          <line x1="5" y1="11" x2="19" y2="11" strokeLinecap="round" />
          <path d="M5 11 Q5 18 12 18" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="8" y1="6" x2="5" y2="18" strokeLinecap="round" />
          <path d="M9 14 L17 6" strokeLinecap="round" />
        </svg>
      ),
      label: "Total Revenue",
      value: totalRevenueLoading ? "Loading..." : formatCurrency(totalRevenue),
      change: "+17.98%",
      positive: true,
      iconBg: "#0ea5e9",
      loading: totalRevenueLoading,
      customHandler: handleTotalRevenueClick,
      permission: "total_revenue",
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="6" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      ),
      label: "Pending Orders",
      value: formatNumber(orderStats.pendingOrders),
      change: "-5.21%",
      positive: false,
      iconBg: theme.warning,
      loading: loading.orders,
      customHandler: handlePendingOrdersClick,
      permission: ["orders", "orders.view"],
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ),
      label: "Completed Orders",
      value: formatNumber(orderStats.completedOrders),
      change: "+8.32%",
      positive: true,
      iconBg: theme.success,
      loading: loading.orders,
      subtitle: "Delivered",
      subValue: formatNumber(orderStats.completedOrders),
      customHandler: handleCompletedOrdersClick,
      permission: ["orders", "orders.view"],
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      ),
      label: "Cancelled Orders",
      value: formatNumber(cancelledOrders),
      change: "-2.15%",
      positive: true,
      iconBg: theme.error || theme.danger,
      loading: loading.orders,
      customHandler: handleCancelledOrdersClick,
      permission: ["orders", "orders.view"],
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
      label: "Delivery Riders",
      value: formatNumber(riderStats.totalRiders),
      change: "+4.35%",
      positive: true,
      iconBg: "#8b5cf6",
      loading: loading.riders,
      subtitle: "Active",
      subValue: formatNumber(riderStats.activeRiders),
      customHandler: handleDeliveryRidersClick,
      permission: ["drivers", "drivers.overview"],
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
          <path d="M4 4l2 2" />
          <path d="M20 4l-2 2" />
        </svg>
      ),
      label: "Online Riders",
      value: formatNumber(riderStats.onRiders),
      change: "+8.75%",
      positive: true,
      iconBg: theme.success,
      loading: loading.riders,
      subtitle: "Online",
      subValue: formatNumber(riderStats.onRiders),
      customHandler: handleOnlineRidersClick,
      permission: ["drivers", "drivers.overview"],
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
      label: "Total Restaurants",
      value: formatNumber(restaurantStats.totalRestaurants),
      change: "+12.5%",
      positive: true,
      iconBg: theme.success,
      loading: loading.restaurants,
      subtitle: "Active",
      subValue: formatNumber(restaurantStats.activeRestaurants),
      customHandler: handleRestaurantClick,
      permission: ["restaurants", "restaurants.overview"],
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v4M12 18v4M2 12h4M18 12h4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        </svg>
      ),
      label: "Live Restaurants",
      value: formatNumber(restaurantStats.onRestaurants),
      change: "+5.2%",
      positive: true,
      iconBg: theme.success,
      loading: loading.restaurants,
      subtitle: "Online Now",
      subValue: formatNumber(restaurantStats.onRestaurants),
      customHandler: handleLiveRestaurantsClick,
      permission: ["restaurants", "restaurants.overview"],
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      label: "Customers",
      value: formatNumber(userStats.totalUsers),
      change: "+7.21%",
      positive: true,
      iconBg: "#ec4899",
      loading: loading.users,
      subtitle: "Active",
      subValue: formatNumber(userStats.activeUsers),
      customHandler: handleCustomersClick,
      permission: ["users", "users.view"],
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        </svg>
      ),
      label: "Today's Orders",
      value: formatNumber(todayStats.todayOrders),
      change: "+12.4%",
      positive: true,
      iconBg: "#f97316",
      loading: loading.today,
      subtitle: "Today",
      subValue: formatNumber(todayStats.todayOrders),
      customHandler: handleTodayOrdersClick,
      permission: ["orders", "orders.view"],
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="5" y1="6" x2="19" y2="6" strokeLinecap="round" />
          <line x1="5" y1="11" x2="19" y2="11" strokeLinecap="round" />
          <path d="M5 11 Q5 18 12 18" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="8" y1="6" x2="5" y2="18" strokeLinecap="round" />
          <path d="M9 14 L17 6" strokeLinecap="round" />
        </svg>
      ),
      label: "Today's Income",
      value: todayStats.todayIncome.toFixed(1) + "K",
      change: "+18.6%",
      positive: true,
      iconBg: "#06b6d4",
      loading: loading.today,
      subtitle: "Today",
      subValue: todayStats.todayIncome.toFixed(1) + "K",
      customHandler: handleTodayIncomeClick,
      permission: "total_revenue",
    },
  ];

  // Filter stats based on permissions
  const filteredStats = stats.filter((stat) => {
    if (!stat.permission) return true;
    if (Array.isArray(stat.permission)) {
      return hasAnyPermission(stat.permission);
    }
    return hasPermission(stat.permission);
  });

  const getStatsGrid = () => {
    if (isMobile) return "1fr";
    if (isTablet) return "repeat(2, 1fr)";
    return "repeat(4, 1fr)";
  };

  const getMiddleGrid = () => {
    if (isMobile) return "1fr";
    if (isTablet) return "repeat(2, 1fr)";
    return "1.5fr 1fr";
  };

  const getBottomGrid = () => {
    if (isMobile) return "1fr";
    if (isTablet) return "repeat(2, 1fr)";
    return "repeat(2, 1fr)";
  };

  const currentRevenueData =
    selectedPeriod === "weekly" ? chartData.weekly : chartData.monthly;
  const categoryChartData = chartData.category.map((item, index) => ({ ...item, color: CHART_COLORS[index % CHART_COLORS.length] }));
  const paymentChartData = chartData.payment.map((item, index) => ({ ...item, color: CHART_COLORS[index % CHART_COLORS.length] }));

  return (
    <div
      style={{
        minHeight: "100vh",
        background: theme.background,
        fontFamily: "'DM Sans', sans-serif",
        transition: "background 0.3s ease",
      }}
    >
      <style>
        {`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes liveDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
      `}
      </style>
      <div
        style={{
          padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
        }}
      >
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{
              margin: 0,
              fontSize: isMobile ? "20px" : "24px",
              fontWeight: 700,
              color: theme.textPrimary,
            }}
          >
            Dashboard
          </h1>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: "13px",
              color: theme.textMuted,
            }}
          >
            Welcome back! Here's what's happening with your business today.
          </p>
        </div>

        {/* ===================== STATS GRID ===================== */}
        {filteredStats.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              background: theme.surface,
              borderRadius: "16px",
              border: `1px solid ${theme.border}`,
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                background: `${theme.primary}1a`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                fontSize: "32px",
              }}
            >
              🔒
            </div>
            <h3
              style={{
                color: theme.textPrimary,
                marginBottom: "8px",
                fontSize: "16px",
              }}
            >
              Access Restricted
            </h3>
            <p style={{ color: theme.textMuted, fontSize: "14px", margin: 0 }}>
              You don't have permission to view any dashboard statistics.
            </p>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: getStatsGrid(),
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            {filteredStats.map((stat, idx) => (
              <StatCard
                key={idx}
                {...stat}
                theme={theme}
                onClick={stat.customHandler}
              />
            ))}
          </div>
        )}

        {/* ===================== MIDDLE SECTION ===================== */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: getMiddleGrid(),
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          {/* Revenue Chart */}
          {hasPermission("total_revenue") && (
            <Card
              style={{
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: "16px",
                boxShadow: `0 4px 12px ${theme.primary}08`,
              }}
            >
              <CardHeader>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <div>
                    <CardTitle style={{ color: theme.textPrimary }}>
                      Revenue Overview
                    </CardTitle>
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "11px",
                        color: theme.textMuted,
                      }}
                    >
                      Order volume & earnings
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Button
                      size="sm"
                      variant={selectedPeriod === "weekly" ? "default" : "outline"}
                      onClick={() => setSelectedPeriod("weekly")}
                      style={{
                        background:
                          selectedPeriod === "weekly"
                            ? theme.primary
                            : "transparent",
                        color:
                          selectedPeriod === "weekly"
                            ? "#fff"
                            : theme.textSecondary,
                        borderColor: theme.border,
                        height: "28px",
                      }}
                    >
                      Weekly
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedPeriod === "monthly" ? "default" : "outline"}
                      onClick={() => setSelectedPeriod("monthly")}
                      style={{
                        background:
                          selectedPeriod === "monthly"
                            ? theme.primary
                            : "transparent",
                        color:
                          selectedPeriod === "monthly"
                            ? "#fff"
                            : theme.textSecondary,
                        borderColor: theme.border,
                        height: "28px",
                      }}
                    >
                      Monthly
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer
                  width="100%"
                  height={isMobile ? 200 : 280}
                >
                  <AreaChart
                    data={currentRevenueData}
                    margin={{ top: 10, right: 0, left: -15, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="revenueGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={theme.primary}
                          stopOpacity={0.35}
                        />
                        <stop
                          offset="95%"
                          stopColor={theme.primary}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={theme.border}
                      strokeOpacity={0.3}
                    />
                    <XAxis
                      dataKey={selectedPeriod === "weekly" ? "day" : "month"}
                      tick={{ fontSize: 10, fill: theme.textMuted }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 9, fill: theme.textMuted }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `${v / 1000}k`}
                    />
                    <RechartsTooltip
                      content={<ChartTip theme={theme} />}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      name="Revenue"
                      stroke={theme.primary}
                      strokeWidth={2.5}
                      fill="url(#revenueGradient)"
                    />
                    <Area
                      type="monotone"
                      dataKey="orders"
                      name="Orders"
                      stroke={theme.success}
                      strokeWidth={2}
                      fill="none"
                      strokeDasharray="4 2"
                    />
                  </AreaChart>
                </ResponsiveContainer>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "20px",
                    marginTop: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <div
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "2px",
                        background: theme.primary,
                      }}
                    />
                    <span
                      style={{ fontSize: "11px", color: theme.textMuted }}
                    >
                      Revenue
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <div
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "2px",
                        background: theme.success,
                      }}
                    />
                    <span
                      style={{ fontSize: "11px", color: theme.textMuted }}
                    >
                      Orders
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Live Orders */}
          {hasAnyPermission(["orders", "orders.view"]) && (
            <Card
              style={{
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: "16px",
                boxShadow: `0 4px 12px ${theme.primary}08`,
              }}
            >
              <CardHeader>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <div>
                    <CardTitle style={{ color: theme.textPrimary }}>
                      🔄 Live Orders
                    </CardTitle>
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "11px",
                        color: theme.textMuted,
                      }}
                    >
                      {liveOrdersLoading
                        ? "Loading..."
                        : `${liveOrders.length} recent orders`}
                    </p>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: theme.success,
                        animation: "liveDot 1.5s ease-in-out infinite",
                      }}
                    />
                    <Badge
                      style={{
                        background: `${theme.success}1a`,
                        color: theme.success,
                        border: "none",
                        fontSize: "10px",
                      }}
                    >
                      Live
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent
                style={{
                  maxHeight: isMobile ? "300px" : "400px",
                  overflowY: "auto",
                }}
              >
                {liveOrdersLoading ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: theme.textMuted,
                    }}
                  >
                    Loading orders...
                  </div>
                ) : liveOrders.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: theme.textMuted,
                    }}
                  >
                    No orders found
                  </div>
                ) : (
                  liveOrders.map((order) => (
                    <LiveOrderCard
                      key={order.id}
                      order={order}
                      theme={theme}
                    />
                  ))
                )}
                <Button
                  variant="outline"
                  size="sm"
                  style={{
                    width: "100%",
                    marginTop: "10px",
                    background: theme.surfaceLighter,
                    borderColor: theme.border,
                    color: theme.textSecondary,
                  }}
                  onClick={handleTotalOrdersClick}
                >
                  View All Orders →
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ===================== BOTTOM SECTION ===================== */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: getBottomGrid(),
            gap: "16px",
          }}
        >
          {/* Category Distribution */}
          {hasAnyPermission(["restaurants", "restaurants.overview"]) && (
            <Card
              style={{
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: "16px",
                boxShadow: `0 4px 12px ${theme.primary}08`,
              }}
            >
              <CardHeader>
                <CardTitle style={{ color: theme.textPrimary }}>
                  Category Distribution
                </CardTitle>
                <p
                  style={{
                    margin: "2px 0 0",
                    fontSize: "11px",
                    color: theme.textMuted,
                  }}
                >
                  Sales by category
                </p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer
                  width="100%"
                  height={isMobile ? 160 : 200}
                >
                  <PieChart>
                    <Pie
                      data={categoryChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={isMobile ? 40 : 50}
                      outerRadius={isMobile ? 70 : 85}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="none"
                    >
                      {categoryChartData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      content={<ChartTip theme={theme} />}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "12px",
                    marginTop: "10px",
                  }}
                >
                  {categoryChartData.map((c) => (
                    <div
                      key={c.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "2px",
                          background: c.color,
                        }}
                      />
                      <span
                        style={{ fontSize: "11px", color: theme.textMuted }}
                      >
                        {c.name}
                      </span>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: theme.textPrimary,
                        }}
                      >
                        ₹{formatCurrency(c.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Order Status */}
          {hasAnyPermission(["orders", "orders.view"]) && (
            <Card
              style={{
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: "16px",
                boxShadow: `0 4px 12px ${theme.primary}08`,
              }}
            >
              <CardHeader>
                <CardTitle style={{ color: theme.textPrimary }}>
                  Order Status
                </CardTitle>
                <p
                  style={{
                    margin: "2px 0 0",
                    fontSize: "11px",
                    color: theme.textMuted,
                  }}
                >
                  Current order distribution • Total: {orderStats.totalOrders}
                </p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer
                  width="100%"
                  height={isMobile ? 160 : 200}
                >
                  <BarChart
                    data={getOrderStatusData()}
                    layout="vertical"
                    margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={theme.border}
                      strokeOpacity={0.3}
                    />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10, fill: theme.textMuted }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fontSize: 11, fill: theme.textPrimary }}
                      axisLine={false}
                      tickLine={false}
                      width={80}
                    />
                    <RechartsTooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div
                            style={{
                              background: theme.surface,
                              border: `1px solid ${theme.border}`,
                              borderRadius: "10px",
                              padding: "10px 14px",
                              boxShadow: `0 8px 32px ${theme.primary}20`,
                              fontSize: "12px",
                            }}
                          >
                            <p
                              style={{
                                color: theme.textPrimary,
                                fontWeight: 600,
                                marginBottom: "4px",
                              }}
                            >
                              {label}
                            </p>
                            {payload.map((p, i) => (
                              <p
                                key={i}
                                style={{
                                  color: p.color,
                                  fontWeight: 600,
                                  margin: "2px 0",
                                }}
                              >
                                {p.name}: {p.value}
                              </p>
                            ))}
                          </div>
                        );
                      }}
                    />
                    <Bar
                      dataKey="value"
                      name="Orders"
                      radius={[0, 4, 4, 0]}
                    >
                      {getOrderStatusData().map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "16px",
                    marginTop: "10px",
                  }}
                >
                  {getOrderStatusData().map((item) => (
                    <div
                      key={item.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "2px",
                          background: item.color,
                        }}
                      />
                      <span
                        style={{ fontSize: "11px", color: theme.textMuted }}
                      >
                        {item.name}
                      </span>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: theme.textPrimary,
                        }}
                      >
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ===================== PAYMENT METHOD ===================== */}
        {hasPermission("total_revenue") && (
          <div style={{ marginTop: "16px" }}>
            <Card
              style={{
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: "16px",
                boxShadow: `0 4px 12px ${theme.primary}08`,
              }}
            >
              <CardHeader>
                <CardTitle style={{ color: theme.textPrimary }}>
                  Payment Method Distribution
                </CardTitle>
                <p
                  style={{
                    margin: "2px 0 0",
                    fontSize: "11px",
                    color: theme.textMuted,
                  }}
                >
                  Revenue by payment type
                </p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer
                  width="100%"
                  height={isMobile ? 200 : 250}
                >
                  <BarChart
                    data={paymentChartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={theme.border}
                      strokeOpacity={0.3}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 12, fill: theme.textPrimary }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: theme.textMuted }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip
                      content={<ChartTip theme={theme} />}
                    />
                    <Bar
                      dataKey="value"
                      name="Revenue"
                      radius={[8, 8, 0, 0]}
                    >
                      {paymentChartData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardHome;
